import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { PaymentCurrency, PaymentDetailRecord, PaymentOrderStatus, PaymentScheduleSnapshotRecord } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { useShellStore } from '@renderer/app/state/shell-store';
import { EditorBodyLayout, EditorHeroCard, EditorPageLayout, EditorSectionCard } from '@renderer/shared/components/editor-scaffold';
import { Icon } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  ValuePair
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { useRegisterEditorSaveHandler } from '@renderer/shared/hooks/use-register-editor-save-handler';
import { areDraftsEqual } from '@renderer/shared/utils/draft-equality';
import { createContract } from '@renderer/domains/contracts/contracts-repository';
import {
  deletePaymentOrders,
  getPaymentOrderById,
  refreshPaymentOrder,
  syncPaymentOrder,
  updatePaymentOrder
} from './payments-repository';
import { selectPaymentEditorModel } from './payments-selectors';
import { usePaymentsViewStore } from './payments-view-store';

interface PaymentDraft {
  title: string;
  serviceSummary: string;
  amount: string;
  currency: PaymentCurrency;
  status: PaymentOrderStatus;
  projectId: string;
  contractId: string;
  invoiceId: string;
  expiresAt: string;
  paymentScheduleCurrentLabel: string;
  paymentScheduleTotalAmount: string;
  paymentScheduleRemainingAmount: string;
}

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string | null {
  return value ? `${value}T12:00:00.000Z` : null;
}

function amountMinorToInput(value: number | null): string {
  return value === null ? '' : String(value / 100);
}

function getPaymentScheduleSnapshot(detail: PaymentDetailRecord): PaymentScheduleSnapshotRecord {
  const value = detail.paymentOrder.metadata.paymentScheduleSnapshot;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Partial<PaymentScheduleSnapshotRecord>;
    if (typeof record.currentLabel === 'string' && typeof record.currentAmountMinor === 'number') {
      return {
        totalAmountMinor: typeof record.totalAmountMinor === 'number' ? record.totalAmountMinor : null,
        currency: record.currency ?? detail.paymentOrder.currency,
        currentLabel: record.currentLabel,
        currentAmountMinor: record.currentAmountMinor,
        remainingLabel: typeof record.remainingLabel === 'string' ? record.remainingLabel : 'Plata finala',
        remainingAmountMinor: typeof record.remainingAmountMinor === 'number' ? record.remainingAmountMinor : null,
        invoiceStatusLabel:
          typeof record.invoiceStatusLabel === 'string' ? record.invoiceStatusLabel : 'Factura: urmeaza sa fie emisa',
        paymentNotes: typeof record.paymentNotes === 'string' ? record.paymentNotes : null,
        scheduleSummary: typeof record.scheduleSummary === 'string' ? record.scheduleSummary : null
      };
    }
  }

  return {
    totalAmountMinor: null,
    currency: detail.paymentOrder.currency,
    currentLabel: 'Avans / Transa 1',
    currentAmountMinor: detail.paymentOrder.amountMinor,
    remainingLabel: 'Plata finala',
    remainingAmountMinor: null,
    invoiceStatusLabel: 'Factura: urmeaza sa fie emisa',
    paymentNotes: null,
    scheduleSummary: null
  };
}

function toDraft(detail: PaymentDetailRecord): PaymentDraft {
  const schedule = getPaymentScheduleSnapshot(detail);

  return {
    title: detail.paymentOrder.title,
    serviceSummary: detail.paymentOrder.serviceSummary,
    amount: String(detail.paymentOrder.amountMinor / 100),
    currency: detail.paymentOrder.currency,
    status: detail.paymentOrder.status,
    projectId: detail.paymentOrder.projectId ?? '',
    contractId: detail.paymentOrder.contractId ?? '',
    invoiceId: detail.paymentOrder.invoiceId ?? '',
    expiresAt: formatDateInputValue(detail.paymentOrder.expiresAt),
    paymentScheduleCurrentLabel: schedule.currentLabel,
    paymentScheduleTotalAmount: amountMinorToInput(schedule.totalAmountMinor),
    paymentScheduleRemainingAmount: amountMinorToInput(schedule.remainingAmountMinor)
  };
}

export function PaymentEditorScreen(): JSX.Element {
  const navigate = useNavigate();
  const { paymentOrderId } = useParams();
  const { locale, t } = useI18n();
  const setRouteDetailTitle = useShellStore((state) => state.setRouteDetailTitle);
  const { editorTab, setEditorTab } = usePaymentsViewStore();
  const [detail, setDetail] = useState<PaymentDetailRecord | null>(null);
  const [draft, setDraft] = useState<PaymentDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const savedDraft = useMemo(() => (detail ? toDraft(detail) : null), [detail]);
  const isDirty = draft && savedDraft ? !areDraftsEqual(draft, savedDraft) : false;

  useEffect(() => {
    if (!paymentOrderId) {
      setError('Missing payment order id.');
      return;
    }

    let active = true;
    void getPaymentOrderById(paymentOrderId)
      .then((nextDetail) => {
        if (!active) {
          return;
        }

        if (!nextDetail) {
          setError('Payment order not found.');
          return;
        }

        setDetail(nextDetail);
        setDraft(toDraft(nextDetail));
        setRouteDetailTitle(nextDetail.paymentOrder.title);
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Payment order unavailable.');
        }
      });

    return () => {
      active = false;
      setRouteDetailTitle(null);
    };
  }, [paymentOrderId, setRouteDetailTitle]);

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detailRequest) => {
        if (detailRequest.domain !== 'payments' || !detail || !detailRequest.ids.includes(detail.paymentOrder.id)) {
          return;
        }

        void deletePaymentOrders({ ids: [detail.paymentOrder.id] }).then(() => navigate('/payments'));
      }),
    [detail, navigate]
  );

  useRegisterEditorSaveHandler(
    detail && isDirty
      ? () => {
          if (!isSaving) {
            void handleSave();
          }
        }
      : null
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('payments.title')} description={error} />
      </div>
    );
  }

  if (!detail || !draft) {
    return <div className="av-page" />;
  }

  const model = selectPaymentEditorModel(detail, { locale, t });
  const paymentOrder = detail.paymentOrder;
  const paymentSchedule = getPaymentScheduleSnapshot(detail);
  const customerContracts = detail.contractSummaries.filter((contract) => contract.customerId === paymentOrder.customerId);
  const isPublicLinkReady = paymentOrder.syncStatus === 'synced';
  const projectOptions = [
    { value: '', label: t('common.notSet') },
    ...detail.projectSummaries
      .filter((project) => project.customerId === paymentOrder.customerId)
      .map((project) => ({ value: project.id, label: project.name }))
  ];
  const contractOptions = [
    { value: '', label: t('common.notSet') },
    ...customerContracts.map((contract) => ({ value: contract.id, label: `${contract.contractNumber} - ${contract.title}` }))
  ];
  const invoiceOptions = [
    { value: '', label: t('common.notSet') },
    ...detail.invoiceSummaries
      .filter((invoice) => invoice.customerId === paymentOrder.customerId)
      .map((invoice) => ({ value: invoice.id, label: invoice.label }))
  ];
  const statusOptions = (
    [
      'draft',
      'ready',
      'sent',
      'viewed',
      'accepted',
      'checkout_started',
      'paid',
      'payment_failed',
      'cancelled',
      'expired',
      'archived'
    ] satisfies PaymentOrderStatus[]
  ).map((status) => ({ value: status, label: t(`enums.paymentStatus.${status}`) }));

  async function handleSave(): Promise<void> {
    if (!detail || !draft) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const amountMajor = Number(draft.amount);
      if (!Number.isFinite(amountMajor) || amountMajor <= 0) {
        throw new Error(t('payments.errors.invalidAmount'));
      }
      const totalAmountMajor = draft.paymentScheduleTotalAmount.trim() ? Number(draft.paymentScheduleTotalAmount) : null;
      const remainingAmountMajor = draft.paymentScheduleRemainingAmount.trim() ? Number(draft.paymentScheduleRemainingAmount) : null;
      if (totalAmountMajor !== null && (!Number.isFinite(totalAmountMajor) || totalAmountMajor <= 0)) {
        throw new Error(t('payments.errors.invalidAmount'));
      }
      if (remainingAmountMajor !== null && (!Number.isFinite(remainingAmountMajor) || remainingAmountMajor < 0)) {
        throw new Error(t('payments.errors.invalidAmount'));
      }

      const nextDetail = await updatePaymentOrder({
        id: detail.paymentOrder.id,
        title: draft.title.trim(),
        serviceSummary: draft.serviceSummary.trim(),
        amountMinor: Math.round(amountMajor * 100),
        currency: draft.currency,
        status: draft.status,
        projectId: draft.projectId || null,
        contractId: draft.contractId || null,
        invoiceId: draft.invoiceId || null,
        expiresAt: toIsoDate(draft.expiresAt),
        paymentScheduleCurrentLabel: draft.paymentScheduleCurrentLabel.trim(),
        paymentScheduleTotalAmountMinor: totalAmountMajor === null ? null : Math.round(totalAmountMajor * 100),
        paymentScheduleRemainingAmountMinor:
          remainingAmountMajor === null ? null : Math.round(remainingAmountMajor * 100)
      });
      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
      setRouteDetailTitle(nextDetail.paymentOrder.title);
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSync(): Promise<void> {
    setIsSaving(true);
    setSaveError(null);

    try {
      const nextDetail = await syncPaymentOrder(paymentOrder.id);
      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Sync failed.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRefresh(): Promise<void> {
    setIsSaving(true);
    setSaveError(null);

    try {
      const nextDetail = await refreshPaymentOrder(paymentOrder.id);
      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Refresh failed.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCopyUrl(): Promise<void> {
    if (!isPublicLinkReady) {
      setSaveError(t('payments.errors.linkNotSynced'));
      return;
    }

    await navigator.clipboard.writeText(paymentOrder.publicUrl);
  }

  async function handleCreateContractForPayment(): Promise<void> {
    if (!detail || !draft) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const amountMajor = Number(draft.amount);
      if (!Number.isFinite(amountMajor) || amountMajor <= 0) {
        throw new Error(t('payments.errors.invalidAmount'));
      }
      const totalAmountMajor = draft.paymentScheduleTotalAmount.trim() ? Number(draft.paymentScheduleTotalAmount) : null;
      const remainingAmountMajor = draft.paymentScheduleRemainingAmount.trim() ? Number(draft.paymentScheduleRemainingAmount) : null;
      if (totalAmountMajor !== null && (!Number.isFinite(totalAmountMajor) || totalAmountMajor <= 0)) {
        throw new Error(t('payments.errors.invalidAmount'));
      }
      if (remainingAmountMajor !== null && (!Number.isFinite(remainingAmountMajor) || remainingAmountMajor < 0)) {
        throw new Error(t('payments.errors.invalidAmount'));
      }

      const contractDetail = await createContract({
        customerId: paymentOrder.customerId,
        title: draft.title.trim() || paymentOrder.title,
        projectId: draft.projectId || paymentOrder.projectId
      });
      const nextDetail = await updatePaymentOrder({
        id: detail.paymentOrder.id,
        title: draft.title.trim(),
        serviceSummary: draft.serviceSummary.trim(),
        amountMinor: Math.round(amountMajor * 100),
        currency: draft.currency,
        status: draft.status,
        projectId: contractDetail.contract.projectId,
        contractId: contractDetail.contract.id,
        invoiceId: draft.invoiceId || null,
        expiresAt: toIsoDate(draft.expiresAt),
        paymentScheduleCurrentLabel: draft.paymentScheduleCurrentLabel.trim(),
        paymentScheduleTotalAmountMinor: totalAmountMajor === null ? null : Math.round(totalAmountMajor * 100),
        paymentScheduleRemainingAmountMinor:
          remainingAmountMajor === null ? null : Math.round(remainingAmountMajor * 100)
      });

      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
      setRouteDetailTitle(nextDetail.paymentOrder.title);
      setEditorTab('contract');
    } catch (nextError) {
      setSaveError(nextError instanceof Error ? nextError.message : 'Contract creation failed.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!window.confirm(t('common.confirmDeleteSoft'))) {
      return;
    }

    await deletePaymentOrders({ ids: [paymentOrder.id] });
    navigate('/payments');
  }

  return (
    <EditorPageLayout
      hero={
        <EditorHeroCard
          actions={
            <div className="av-editor-actions">
              <button className="av-secondary-button" disabled={isSaving || !isPublicLinkReady} type="button" onClick={() => void handleCopyUrl()}>
                <Icon name="duplicate" />
                <span>{t('payments.actions.copyUrl')}</span>
              </button>
              {isPublicLinkReady ? (
                <a className="av-secondary-button" href={paymentOrder.publicUrl} rel="noreferrer" target="_blank">
                  <Icon name="website" />
                  <span>{t('payments.actions.openUrl')}</span>
                </a>
              ) : (
                <button className="av-secondary-button" disabled type="button">
                  <Icon name="website" />
                  <span>{t('payments.actions.openUrl')}</span>
                </button>
              )}
              <button className="av-secondary-button" disabled={isSaving} type="button" onClick={() => void handleSync()}>
                <Icon name="sync" />
                <span>{t('payments.actions.sync')}</span>
              </button>
              <button className="av-secondary-button" disabled={isSaving} type="button" onClick={() => void handleRefresh()}>
                <Icon name="update" />
                <span>{t('payments.actions.refresh')}</span>
              </button>
              <button className="av-primary-button" disabled={!isDirty || isSaving} type="button" onClick={() => void handleSave()}>
                <Icon name="check" />
                <span>{t('payments.actions.save')}</span>
              </button>
            </div>
          }
        >
          <div className="av-editor-hero-card__content">
            <span className="av-breadcrumb">
              <Link to="/payments">{t('payments.title')}</Link> / {model.customerName}
            </span>
            <h1>{model.title}</h1>
            <div className="av-editor-hero-card__meta">
              <StatusBadge label={model.statusLabel} tone={model.statusTone} />
              <span>{model.amountLabel}</span>
              {isPublicLinkReady ? (
                <a href={paymentOrder.publicUrl} rel="noreferrer" target="_blank">
                  {paymentOrder.publicUrl}
                </a>
              ) : (
                <span className="av-editor-hero-card__warning">{t('payments.sync.localOnlyWarning')}</span>
              )}
            </div>
            {saveError ? <p className="av-form-error">{saveError}</p> : null}
            {paymentOrder.syncError ? <p className="av-form-error">{paymentOrder.syncError}</p> : null}
          </div>
        </EditorHeroCard>
      }
      heroAside={
        <EditorSectionCard title={t('payments.panels.summary')}>
          <div className="av-value-list">
            <ValuePair label={t('payments.fields.customer')} value={model.customerName} />
            <ValuePair label={t('payments.fields.issuer')} value={model.issuerName} />
            <ValuePair label={t('payments.fields.syncStatus')} value={model.syncLabel} />
            <ValuePair label={t('payments.fields.expiresAt')} value={model.expiresLabel} />
            <ValuePair label={t('payments.fields.installmentLabel')} value={paymentSchedule.currentLabel} />
            <ValuePair label={t('payments.fields.invoiceStatus')} value={paymentSchedule.invoiceStatusLabel} />
            <ValuePair label={t('payments.fields.acceptedAt')} value={model.acceptedLabel} />
            <ValuePair label={t('payments.fields.paidAt')} value={model.paidLabel} />
          </div>
        </EditorSectionCard>
      }
      tabs={
        <EditorTabs
          tabs={[
            { id: 'general', label: t('payments.tabs.general'), icon: 'payments' },
            { id: 'contract', label: t('payments.tabs.contract'), icon: 'contracts' },
            { id: 'evidence', label: t('payments.tabs.evidence'), icon: 'document' }
          ]}
          value={editorTab}
          onChange={setEditorTab}
        />
      }
    >
      {editorTab === 'general' ? (
        <EditorBodyLayout
          main={
            <EditorSectionCard title={t('payments.sections.details')}>
              <div className="av-payment-editor-grid">
                <label className="av-field av-field--span-2">
                  <span>{t('payments.fields.title')}</span>
                  <TextField value={draft.title} onValueChange={(title) => setDraft((current) => (current ? { ...current, title } : current))} />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.amount')}</span>
                  <TextField value={draft.amount} inputMode="decimal" onValueChange={(amount) => setDraft((current) => (current ? { ...current, amount } : current))} />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.currency')}</span>
                  <SelectField
                    options={(['RON', 'EUR', 'USD'] satisfies PaymentCurrency[]).map((currency) => ({ value: currency, label: currency }))}
                    value={draft.currency}
                    onChange={(event) => setDraft((current) => (current ? { ...current, currency: event.target.value as PaymentCurrency } : current))}
                  />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.installmentLabel')}</span>
                  <TextField
                    value={draft.paymentScheduleCurrentLabel}
                    onValueChange={(paymentScheduleCurrentLabel) =>
                      setDraft((current) => (current ? { ...current, paymentScheduleCurrentLabel } : current))
                    }
                  />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.contractTotal')}</span>
                  <TextField
                    value={draft.paymentScheduleTotalAmount}
                    inputMode="decimal"
                    onValueChange={(paymentScheduleTotalAmount) =>
                      setDraft((current) => (current ? { ...current, paymentScheduleTotalAmount } : current))
                    }
                  />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.finalPaymentRemaining')}</span>
                  <TextField
                    value={draft.paymentScheduleRemainingAmount}
                    inputMode="decimal"
                    onValueChange={(paymentScheduleRemainingAmount) =>
                      setDraft((current) => (current ? { ...current, paymentScheduleRemainingAmount } : current))
                    }
                  />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.status')}</span>
                  <SelectField
                    options={statusOptions}
                    value={draft.status}
                    onChange={(event) => setDraft((current) => (current ? { ...current, status: event.target.value as PaymentOrderStatus } : current))}
                  />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.expiresAt')}</span>
                  <TextField type="date" value={draft.expiresAt} onValueChange={(expiresAt) => setDraft((current) => (current ? { ...current, expiresAt } : current))} />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.project')}</span>
                  <SelectField
                    options={projectOptions}
                    value={draft.projectId}
                    onChange={(event) => setDraft((current) => (current ? { ...current, projectId: event.target.value } : current))}
                  />
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.contract')}</span>
                  <SelectField
                    options={contractOptions}
                    value={draft.contractId}
                    onChange={(event) => setDraft((current) => (current ? { ...current, contractId: event.target.value } : current))}
                  />
                  {customerContracts.length === 0 ? <em className="av-field-note">{t('payments.empty.noContractsForCustomer')}</em> : null}
                </label>
                <label className="av-field">
                  <span>{t('payments.fields.invoice')}</span>
                  <SelectField
                    options={invoiceOptions}
                    value={draft.invoiceId}
                    onChange={(event) => setDraft((current) => (current ? { ...current, invoiceId: event.target.value } : current))}
                  />
                </label>
                <label className="av-field av-field--span-3">
                  <span>{t('payments.fields.serviceSummary')}</span>
                  <TextAreaField
                    rows={4}
                    value={draft.serviceSummary}
                    onValueChange={(serviceSummary) => setDraft((current) => (current ? { ...current, serviceSummary } : current))}
                  />
                </label>
              </div>
            </EditorSectionCard>
          }
          rail={
            <EditorSectionCard title={t('payments.panels.links')}>
              <div className="av-value-list">
                <ValuePair label={t('payments.fields.publicToken')} value={paymentOrder.publicToken} />
                <ValuePair label={t('payments.fields.checkoutSession')} value={paymentOrder.stripeCheckoutSessionId ?? t('common.notSet')} />
                <ValuePair label={t('payments.fields.paymentIntent')} value={paymentOrder.stripePaymentIntentId ?? t('common.notSet')} />
              </div>
              <button className="av-danger-button" type="button" onClick={() => void handleDelete()}>
                <Icon name="trash" />
                <span>{t('common.remove')}</span>
              </button>
              <button
                className="av-primary-button"
                disabled={isSaving || Boolean(draft.contractId)}
                type="button"
                onClick={() => void handleCreateContractForPayment()}
              >
                <Icon name="contracts" />
                <span>{t('payments.actions.createContract')}</span>
              </button>
            </EditorSectionCard>
          }
        />
      ) : null}

      {editorTab === 'contract' ? (
        <EditorSectionCard title={t('payments.sections.contract')}>
          <div className="av-payment-contract-preview" dangerouslySetInnerHTML={{ __html: paymentOrder.contractSnapshotHtml }} />
          <div className="av-value-list">
            <ValuePair label={t('payments.fields.contractHash')} value={paymentOrder.contractHash} />
            <ValuePair label={t('payments.fields.checkboxText')} value={paymentOrder.acceptanceCheckboxText} />
          </div>
        </EditorSectionCard>
      ) : null}

      {editorTab === 'evidence' ? (
        <EditorBodyLayout
          main={
            <EditorSectionCard title={t('payments.sections.evidence')}>
              {detail.acceptances.length === 0 ? (
                <EmptyState title={t('payments.evidence.emptyTitle')} description={t('payments.evidence.emptyDescription')} />
              ) : (
                <div className="av-payment-evidence-list">
                  {detail.acceptances.map((acceptance) => (
                    <article key={acceptance.id} className="av-payment-evidence-item">
                      <strong>{acceptance.signerName}</strong>
                      <span>{acceptance.signerRole ?? t('common.notSet')}</span>
                      <span>{acceptance.signerEmail ?? t('common.notSet')}</span>
                      <span>{new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(acceptance.acceptedAt))}</span>
                      <span>{acceptance.checkboxTextVersion ?? 'v1'}</span>
                      <span>{acceptance.ipAddress ?? t('common.notSet')}</span>
                      <span>{acceptance.country ?? t('common.notSet')}</span>
                      <span>{acceptance.clientTimeZone ?? t('common.notSet')}</span>
                      <span>
                        {acceptance.clientAcceptedAt
                          ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
                              new Date(acceptance.clientAcceptedAt)
                            )
                          : t('common.notSet')}
                      </span>
                      <span>{acceptance.origin ?? t('common.notSet')}</span>
                      <span>{acceptance.referrer ?? t('common.notSet')}</span>
                      <span>{acceptance.acceptLanguage ?? t('common.notSet')}</span>
                      <span>{acceptance.userAgent ?? t('common.notSet')}</span>
                      <code>{acceptance.ipHash ?? t('common.notSet')}</code>
                      <code>{acceptance.evidenceHash}</code>
                    </article>
                  ))}
                </div>
              )}
            </EditorSectionCard>
          }
          rail={
            <EditorSectionCard title={t('payments.sections.events')}>
              <div className="av-payment-events">
                {detail.events.map((item) => (
                  <div key={item.id} className="av-payment-event">
                    <strong>{item.label}</strong>
                    <span>{new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(item.occurredAt))}</span>
                  </div>
                ))}
              </div>
            </EditorSectionCard>
          }
        />
      ) : null}
    </EditorPageLayout>
  );
}
